import React, { useEffect, useState } from "react";
import Shell from "@/components/layout/Shell";
import { PageHeader, formatCOP } from "@/components/common";
import { api } from "@/lib/api";
import ExpiringQuotes from "@/components/ExpiringQuotes";
import { DollarSign, CalendarClock, Users, FileWarning, TrendingUp, TrendingDown, Clock, ArrowUpRight, ArrowDownRight, Minus, Briefcase, ListChecks, ChevronRight, Bell, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const StatCard = ({ icon: Icon, label, value, hint, testid }) => (
  <div data-testid={testid} className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5 hover:bg-[#1A1D24] transition-colors duration-200">
    <div className="flex items-center justify-between mb-3">
      <div className="w-9 h-9 rounded-xl bg-[#F97316]/10 flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#F97316]" strokeWidth={1.75} />
      </div>
    </div>
    <div className="text-2xl md:text-3xl font-bold text-[#F5F5F7] tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
      {value}
    </div>
    <div className="text-xs text-[#9CA3AF] mt-1">{label}</div>
    {hint && <div className="text-[11px] text-[#6B7280] mt-0.5">{hint}</div>}
  </div>
);

const monthLabel = (mk) => {
  const [y, m] = mk.split("-");
  const names = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  return `${names[parseInt(m)-1]} ${y.slice(2)}`;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/summary")
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return <Shell><div className="text-[#9CA3AF]">Cargando…</div></Shell>;
  }

  const chartData = data.revenue_last_6_months.map(m => ({ ...m, label: monthLabel(m.month) }));

  return (
    <Shell>
      <PageHeader title="Dashboard" subtitle="Vista general de tu negocio hoy" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-monthly-revenue" icon={DollarSign} label="Ingresos del mes" value={formatCOP(data.monthly_revenue)} />
        <StatCard testid="stat-today-appointments" icon={CalendarClock} label="Citas de hoy" value={data.today_appointments} />
        <StatCard testid="stat-active-clients" icon={Users} label="Clientes activos" value={data.active_clients} />
        <StatCard testid="stat-pending-invoices" icon={FileWarning} label="Facturas pendientes" value={data.pending_invoices_count} hint={formatCOP(data.pending_invoices_amount)} />
      </div>

      {/* Growth panel */}
      {(() => {
        const g = data.growth || {};
        const gained = g.gained_this_month ?? 0;
        const lost = g.lost_this_month ?? 0;
        const net = g.net_this_month ?? 0;
        const prevNet = g.net_prev_month ?? 0;
        const trend = net - prevNet;
        return (
          <div data-testid="growth-panel" className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 mb-6">
            <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
              <div>
                <h3 className="font-bold text-[#F5F5F7] flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
                  <TrendingUp className="w-4 h-4 text-[#F97316]" strokeWidth={1.75}/> Crecimiento del mes
                </h3>
                <p className="text-xs text-[#9CA3AF] mt-0.5">Clientes ganados vs perdidos este mes</p>
              </div>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                net > 0 ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" :
                net < 0 ? "bg-red-500/10 text-red-400 border border-red-500/20" :
                "bg-zinc-500/10 text-zinc-400 border border-zinc-500/20"
              }`}>
                {net > 0 ? <ArrowUpRight className="w-3.5 h-3.5"/> : net < 0 ? <ArrowDownRight className="w-3.5 h-3.5"/> : <Minus className="w-3.5 h-3.5"/>}
                Neto {net > 0 ? "+" : ""}{net}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div data-testid="growth-gained" className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <ArrowUpRight className="w-4 h-4 text-emerald-400" strokeWidth={2}/>
                  </div>
                  <span className="text-xs text-[#9CA3AF]">Ganados</span>
                </div>
                <div className="text-2xl font-bold text-emerald-400" style={{ fontFamily: "Manrope, sans-serif" }}>+{gained}</div>
                <div className="text-[11px] text-[#6B7280] mt-1">Nuevos activos este mes</div>
              </div>

              <div data-testid="growth-lost" className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <ArrowDownRight className="w-4 h-4 text-red-400" strokeWidth={2}/>
                  </div>
                  <span className="text-xs text-[#9CA3AF]">Perdidos</span>
                </div>
                <div className="text-2xl font-bold text-red-400" style={{ fontFamily: "Manrope, sans-serif" }}>-{lost}</div>
                <div className="text-[11px] text-[#6B7280] mt-1">Finalizados este mes</div>
              </div>

              <div data-testid="growth-trend" className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-[#F97316]/10 flex items-center justify-center">
                    {trend >= 0 ? <TrendingUp className="w-4 h-4 text-[#F97316]"/> : <TrendingDown className="w-4 h-4 text-[#F97316]"/>}
                  </div>
                  <span className="text-xs text-[#9CA3AF]">Vs mes anterior</span>
                </div>
                <div className={`text-2xl font-bold ${trend > 0 ? "text-emerald-400" : trend < 0 ? "text-red-400" : "text-[#F5F5F7]"}`} style={{ fontFamily: "Manrope, sans-serif" }}>
                  {trend > 0 ? "+" : ""}{trend}
                </div>
                <div className="text-[11px] text-[#6B7280] mt-1">Neto mes previo: {prevNet > 0 ? "+" : ""}{prevNet}</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Business Balance panel */}
      {(() => {
        const bb = data.business_balance || {};
        const contracted = bb.contracted_total ?? 0;
        const received = bb.received_total ?? 0;
        const pending = bb.pending_total ?? 0;
        const pct = contracted > 0 ? Math.round((received / (received + pending || 1)) * 100) : 0;
        return (
          <div data-testid="business-balance" className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="w-4 h-4 text-[#F97316]" strokeWidth={1.75}/>
              <h3 className="font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>Balance por negocios</h3>
              <span className="text-xs text-[#9CA3AF] ml-2">Valor contratado, cobrado y pendiente</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-4">
                <div className="text-xs text-[#9CA3AF] mb-1">Contratado</div>
                <div className="text-xl font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>{formatCOP(contracted)}</div>
                <div className="text-[10px] text-[#6B7280] mt-1">M: {formatCOP(bb.contracted_monthly || 0)} · U: {formatCOP(bb.contracted_unico || 0)}</div>
              </div>
              <div className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-4">
                <div className="text-xs text-[#9CA3AF] mb-1">Cobrado</div>
                <div className="text-xl font-bold text-emerald-400" style={{ fontFamily: "Manrope, sans-serif" }}>{formatCOP(received)}</div>
                <div className="text-[10px] text-[#6B7280] mt-1">Facturas cobradas</div>
              </div>
              <div className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-4">
                <div className="text-xs text-[#9CA3AF] mb-1">Pendiente</div>
                <div className="text-xl font-bold text-amber-400" style={{ fontFamily: "Manrope, sans-serif" }}>{formatCOP(pending)}</div>
                <div className="text-[10px] text-[#6B7280] mt-1">Por cobrar</div>
              </div>
              <div className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-4">
                <div className="text-xs text-[#9CA3AF] mb-1">% cobrado</div>
                <div className="text-xl font-bold text-[#F97316]" style={{ fontFamily: "Manrope, sans-serif" }}>{pct}%</div>
                <div className="h-1.5 rounded-full bg-[#0B0D12] mt-2 overflow-hidden border border-[#262A33]">
                  <div className="h-full bg-[#F97316] transition-all" style={{ width: `${pct}%` }}/>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Upcoming charges panel */}
      {(data.upcoming_charges || []).length > 0 && (
        <div data-testid="upcoming-charges" className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-[#F97316]" strokeWidth={1.75}/>
              <h3 className="font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>Próximos cobros</h3>
              <span className="text-xs text-[#9CA3AF] ml-1">{data.upcoming_charges.length} pendientes</span>
            </div>
            <button onClick={()=>navigate("/pagos")} className="text-xs text-[#F97316] hover:underline">Ver todos →</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.upcoming_charges.map(inv => {
              const overdue = inv.is_overdue;
              const soon = !overdue && inv.days_left !== null && inv.days_left <= 3;
              return (
                <button
                  key={inv.invoice_id}
                  data-testid={`upcoming-charge-${inv.invoice_id}`}
                  onClick={()=>navigate("/pagos")}
                  className={`text-left flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    overdue ? "bg-red-500/5 border-red-500/25 hover:bg-red-500/10" :
                    soon ? "bg-amber-500/5 border-amber-500/25 hover:bg-amber-500/10" :
                    "bg-[#0B0D12] border-[#262A33] hover:border-[#F97316]/40 hover:bg-[#1A1D24]"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    overdue ? "bg-red-500/10 text-red-400" :
                    soon ? "bg-amber-500/10 text-amber-400" : "bg-[#F97316]/10 text-[#F97316]"
                  }`}>
                    {overdue ? <AlertTriangle className="w-4 h-4"/> : <Bell className="w-4 h-4"/>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#F5F5F7] font-medium truncate">{inv.client_name} · {formatCOP(inv.amount)}</div>
                    <div className="text-[10px] text-[#9CA3AF] truncate">
                      {inv.number} · Vence {inv.due_at}{" "}
                      {overdue ? <span className="text-red-400 font-semibold">· VENCIDA {Math.abs(inv.days_left)}d</span> :
                        inv.days_left === 0 ? <span className="text-amber-400 font-semibold">· HOY</span> :
                        inv.days_left > 0 ? <span className={soon ? "text-amber-400 font-semibold" : "text-[#9CA3AF]"}>· en {inv.days_left}d</span> : null}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Pending tasks panel */}
      <ExpiringQuotes days={15}/>
      {(data.pending_tasks || []).length > 0 && (
        <div data-testid="pending-tasks" className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-[#F97316]" strokeWidth={1.75}/>
              <h3 className="font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>Tareas pendientes</h3>
              <span className="text-xs text-[#9CA3AF] ml-1">{data.pending_tasks.length} próximas</span>
            </div>
            <button onClick={()=>navigate("/proyectos")} className="text-xs text-[#F97316] hover:underline">Ver proyectos →</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.pending_tasks.map(t => (
              <button
                key={t.task_id}
                data-testid={`pending-task-${t.task_id}`}
                onClick={()=>navigate(`/proyectos/${t.project_id}`)}
                className="text-left flex items-center gap-3 p-3 rounded-xl bg-[#0B0D12] border border-[#262A33] hover:border-[#F97316]/40 hover:bg-[#1A1D24] transition-colors group"
              >
                <div className="w-4 h-4 rounded border-2 border-[#3A3F4B] flex-shrink-0"/>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#F5F5F7] truncate">{t.title}</div>
                  <div className="text-[10px] text-[#9CA3AF] truncate">{t.project_name}{t.due_date ? ` · Entrega ${t.due_date}` : ""}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#F97316] transition-colors"/>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#15171E] border border-[#262A33] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>Ingresos - últimos 6 meses</h3>
              <p className="text-xs text-[#9CA3AF]">Facturas cobradas por mes</p>
            </div>
            <TrendingUp className="w-4 h-4 text-[#F97316]" strokeWidth={1.75} />
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262A33" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v)=>`$${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: "#15171E", border: "1px solid #262A33", borderRadius: 12, color: "#F5F5F7" }}
                  formatter={(v)=>[formatCOP(v), "Ingresos"]}
                />
                <Line type="monotone" dataKey="revenue" stroke="#F97316" strokeWidth={2.5} dot={{ fill: "#F97316", r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-6">
          <h3 className="font-bold text-[#F5F5F7] mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>Próximas citas</h3>
          {data.upcoming_appointments.length === 0 ? (
            <div className="text-sm text-[#9CA3AF] py-8 text-center">
              <Clock className="w-6 h-6 mx-auto mb-2 text-[#6B7280]" strokeWidth={1.5} />
              Sin citas programadas
            </div>
          ) : (
            <div className="space-y-3">
              {data.upcoming_appointments.map((a) => {
                const d = new Date(a.scheduled_at);
                return (
                  <div key={a.appointment_id} className="flex items-center gap-3 p-3 rounded-xl bg-[#0B0D12] border border-[#262A33]">
                    <div className="w-10 h-10 rounded-xl bg-[#F97316]/10 flex items-center justify-center flex-shrink-0">
                      <div className="text-center">
                        <div className="text-[9px] text-[#F97316] font-semibold uppercase leading-none">{d.toLocaleString("es-CO",{month:"short"})}</div>
                        <div className="text-sm text-[#F5F5F7] font-bold leading-tight">{d.getDate()}</div>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[#F5F5F7] font-medium truncate">{a.title}</div>
                      <div className="text-xs text-[#9CA3AF] truncate">{a.client_name} · {d.toLocaleTimeString("es-CO",{hour:"2-digit",minute:"2-digit"})}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}
